import { FC, Fragment, useMemo } from 'react';
import { TableProps } from 'antd';
import { Fx, Layout, Table } from 'Components';
import { DownloadReportButton, LayoutHolder } from 'Pages/Dashboard/components';
import { OverviewGroupSummary } from 'Services/Api/Dashboard/Types';
import { getGroupSummaryColumns } from './columns';
import Heading from '../components/Heading';
import Button from '../components/Button';
import styles from './GroupSummaryTable.module.scss';
import { ReportType } from 'Services/Api/Reports/Types';
import TableDataCard from './TableDataCard';
import { useIsMobile } from 'Hooks';
import { useTranslation } from 'react-i18next';
import { FEATURE_ACCESS_TYPE } from 'FeatureManager/FeatureManager.type';

interface GroupSummaryTableProps {
	data?: OverviewGroupSummary[];
	loading?: boolean;
	title?: string;
	featureAccess: FEATURE_ACCESS_TYPE,
	onChange?: TableProps<OverviewGroupSummary>['onChange'];
}

const GroupSummaryTable: FC<GroupSummaryTableProps> = ({
	data = [],
	loading = false,
	title = 'newDashboard.summaryTable.title',
	featureAccess,
	onChange
}) => {
	const isMobile = useIsMobile();
    const { t } = useTranslation();

	const tableColumns = useMemo(() => {
		const list = getGroupSummaryColumns(t);
	
		const columnAccessMap: Record<string, keyof FEATURE_ACCESS_TYPE> = {
			phishingScore: 'phishing',
			elearningScore: 'training',
			saqStatus: 'saqCompliance',
			scanningStatus: 'scanning'
		};
	
		return list.filter(item => {
			const accessKey = columnAccessMap[item.key as string];
	
			// Columns without a feature restriction are always visible
			if (!accessKey) return true;
	
			return featureAccess[accessKey];
		});
	}, [t, featureAccess]);

	return (
		<LayoutHolder className={styles.tableHolder}>
			<Layout>
				<Layout.Heading className={styles.heading}>
					<Fx align="middle" justify="space-between">
						<Heading title={t(title)} />
						<DownloadReportButton
							reportType={ReportType.OverviewGroupSummary}
						/>
					</Fx>
				</Layout.Heading>
				<Fragment key={`${data?.[0]?.saqStatus || 0}-${data?.[0]?.scanningStatus || 0}`}>
					{isMobile ? data.map((item, index) => <TableDataCard data={item} {...{ loading, featureAccess }} key={index}  />) : <Table<OverviewGroupSummary>
						className={styles.table}
						rowKey="groupName"
						dataSource={data}
						columns={tableColumns}
						pagination={false}
						loading={loading}
						onChange={onChange}
					/>}
				</Fragment>
			</Layout>
		</LayoutHolder>
	);
};

export default GroupSummaryTable;
